import { Download, FileJson, FolderTree, SlidersHorizontal, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getRuntimeConfig } from '@nucleus-commerce/admin-kit/config';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { DocsToc } from '../components/docs-shell-toc';
import type { GuideHeading } from '../content';

interface DocsDownload {
  key: 'collection' | 'environment' | 'spec';
  file: string;
  icon: LucideIcon;
}

/** Written by docs-gen into `public/docs/`, served next to the app. */
const downloads: DocsDownload[] = [
  { key: 'collection', file: 'nucleus-storefront.postman_collection.json', icon: FolderTree },
  {
    key: 'environment',
    file: 'nucleus-storefront.postman_environment.json',
    icon: SlidersHorizontal,
  },
  { key: 'spec', file: 'storefront.openapi.json', icon: FileJson },
];

const variables = ['baseUrl', 'apiKey', 'customerToken'] as const;
const steps = ['download', 'import', 'environment', 'values', 'send', 'customer'] as const;

function SectionHeading({ id, children }: { id: string; children: string }) {
  return (
    <h2
      id={id}
      className="mt-12 scroll-m-20 border-b pb-2 text-2xl font-semibold tracking-tight first:mt-0"
    >
      {children}
    </h2>
  );
}

/** Downloads for Postman (and the OpenAPI file for other tools), the variables, import steps. */
export function PostmanPage() {
  const { t } = useTranslation('docs');
  const apiBaseUrl = getRuntimeConfig().apiBaseUrl;
  const base = `${import.meta.env.BASE_URL}docs/`;

  const headings: GuideHeading[] = [
    { depth: 2, id: 'downloads', text: t('postman.downloads.title') },
    { depth: 2, id: 'variables', text: t('postman.variables.title') },
    { depth: 2, id: 'import', text: t('postman.import.title') },
  ];

  return (
    <>
      <article className="mx-auto max-w-[72ch]">
        <header className="mb-10 flex flex-col gap-2">
          <h1 className="text-3xl font-semibold tracking-tight">{t('postman.title')}</h1>
          <p className="text-muted-foreground text-lg">{t('postman.description')}</p>
        </header>

        <SectionHeading id="downloads">{t('postman.downloads.title')}</SectionHeading>
        <ul className="mt-6 flex flex-col divide-y rounded-lg border">
          {downloads.map(({ key, file, icon: Icon }) => (
            <li key={key} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
              <Icon className="text-muted-foreground size-5 shrink-0" aria-hidden />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="font-medium">{t(`postman.downloads.${key}.name`)}</span>
                <span className="text-muted-foreground text-sm">
                  {t(`postman.downloads.${key}.description`)}
                </span>
                <code className="text-muted-foreground truncate font-mono text-xs">{file}</code>
              </div>
              <Button asChild variant="outline" size="sm" className="self-start sm:self-center">
                <a
                  href={`${base}${file}`}
                  download={file}
                  aria-label={t('postman.downloads.action', {
                    name: t(`postman.downloads.${key}.name`),
                  })}
                >
                  <Download aria-hidden />
                  {t('postman.downloads.button')}
                </a>
              </Button>
            </li>
          ))}
        </ul>

        <SectionHeading id="variables">{t('postman.variables.title')}</SectionHeading>
        <p className="mt-5 leading-7">{t('postman.variables.intro')}</p>
        <div className="my-6 rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('postman.variables.variable')}</TableHead>
                <TableHead>{t('postman.variables.value')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {variables.map((name) => (
                <TableRow key={name}>
                  <TableCell className="align-top">
                    <code className="font-mono text-[0.85em]">{name}</code>
                  </TableCell>
                  <TableCell className="whitespace-normal">
                    {t(`postman.variables.${name}`, { apiBaseUrl })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <SectionHeading id="import">{t('postman.import.title')}</SectionHeading>
        <ol className="my-5 ml-6 list-decimal [&>li]:mt-2">
          {steps.map((step) => (
            <li key={step} className="leading-7">
              {t(`postman.import.steps.${step}`)}
            </li>
          ))}
        </ol>
        <p className="text-muted-foreground mt-5 leading-7">{t('postman.import.otherTools')}</p>
      </article>
      <DocsToc headings={headings} />
    </>
  );
}
